import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    url: String,
    state: String
  }

  connect() {
    this.timeout = null
    this.stopTimeout = null
    this.isRecording = false
    this.broadcastTimeout = null
    
    // Listen for recording events from rich-form controller
    this.element.addEventListener("recording:started", this.handleRecordingStarted.bind(this))
    this.element.addEventListener("recording:stopped", this.handleRecordingStopped.bind(this))
    
    // Reset state when form is submitted successfully
    this.element.addEventListener("turbo:submit-end", this.handleFormSubmit.bind(this))
  }

  disconnect() {
    clearTimeout(this.timeout)
    clearTimeout(this.stopTimeout)
    clearTimeout(this.broadcastTimeout)
    this.stop()
    this.element.removeEventListener("recording:started", this.handleRecordingStarted.bind(this))
    this.element.removeEventListener("recording:stopped", this.handleRecordingStopped.bind(this))
    this.element.removeEventListener("turbo:submit-end", this.handleFormSubmit.bind(this))
  }

  handleFormSubmit(event) {
    // Reset state when message is sent successfully
    if (event.detail?.success) {
      clearTimeout(this.timeout)
      clearTimeout(this.stopTimeout)
      clearTimeout(this.broadcastTimeout)
      this.timeout = null
      this.stopTimeout = null
      this.broadcastTimeout = null
      this.isRecording = false
      this.stateValue = null
    }
  }

  handleRecordingStarted() {
    this.isRecording = true
    this.stateValue = "recording"
    clearTimeout(this.timeout)
    clearTimeout(this.stopTimeout)
    clearTimeout(this.broadcastTimeout)
    this.timeout = null
    this.stopTimeout = null
    this.broadcastTimeout = null
    this.broadcast()
  }

  handleRecordingStopped() {
    this.isRecording = false
    this.stop()
  }

  start(event) {
    // Don't override recording state if currently recording
    if (this.isRecording && this.stateValue === "recording") {
      return
    }

    // Determine state: use event param if provided, otherwise default to "typing"
    const state = event.params?.state || "typing"

    // Idle stop: clear "typing" shortly after the last keypress (not 2s after the debounced broadcast).
    clearTimeout(this.stopTimeout)
    this.stopTimeout = setTimeout(() => {
      this.stop()
    }, 700)

    if (this.stateValue !== state) {
      this.stateValue = state
    }

    // Debounce server broadcasts (typing ping) — one burst per pause in typing
    clearTimeout(this.broadcastTimeout)
    this.broadcastTimeout = setTimeout(() => {
      this.broadcast()
    }, 300)
  }

  stop() {
    // Don't stop if we're actually recording
    if (this.isRecording && this.stateValue === "recording") {
      return
    }
    
    this.stateValue = null
    this.broadcast(true)
  }

  broadcast(stop = false) {
    // Only broadcast if URL is set
    if (!this.urlValue) {
      console.warn("Composing controller: URL value not set")
      return
    }

    const csrfToken = document.querySelector("meta[name=csrf-token]")?.content
    if (!csrfToken) {
      console.warn("Composing controller: CSRF token not found")
      return
    }

    // If stop is requested or state is null/empty, send null
    const stateToSend = (stop || !this.stateValue || this.stateValue === "") ? null : this.stateValue

    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        state: stateToSend
      }),
      credentials: "same-origin"
    }).catch(error => {
      console.error("Composing controller: Failed to broadcast state", error)
    })
  }
}

